/**
 * auth-server-vouch: the ONE signal that says "this session belongs to a test
 * account on the dev step-up allowlist", asked of the server, never decided here.
 *
 * ⛔ WHY THIS EXISTS (Paul, ruling on Addendum 6, 3 September 2026, pick A).
 *
 * The firm console has two browser gates in `routes/admin/_layout.tsx`: the W10
 * gate (a staff session with no verified TOTP factor goes to /auth/mfa-enroll,
 * Paul's 11 July ruling) and the AAL-2 gate (has THIS session passed a second
 * factor). The no-code walk bot, `firm-admin@elreign.io`, has no factor on
 * purpose, so an agent can walk the client portal in a browser but never the
 * firm console, and the firm side of the product has only ever been walked
 * through the API. Paul ruled: teach those two gates to accept the server's
 * step-up vouch, dev only, fail-closed.
 *
 * ── WHY IT ASKS THE SERVER INSTEAD OF CHECKING SOMETHING HERE ───────────────
 *
 * There is no honest client-side test for "this is a dev bot". A hostname test
 * is a string an attacker controls, a build flag ships in the bundle, and an
 * `import.meta.env` check is the same fail-open shape as the backend's
 * `ENVIRONMENT` variable, which is UNSET in production and defaults to 'dev'
 * (see `backend/src/lib/auth/step-up-test-accounts.ts` for that whole argument).
 *
 * The server already holds the fact. `POST /v1/auth/refresh-step-up` answers 200
 * without an MFA challenge ONLY for user ids named in `STEP_UP_TEST_USER_IDS`,
 * a setting that exists on the dev onboarding Function App and nowhere else.
 * `backend/scripts/lint-step-up-test-accounts.mjs` fails the build if it ever
 * appears in a production config, and the runtime is closed by ABSENCE, not by a
 * check: unset means an empty allowlist, which no user id can be in. On
 * production this call answers 403 for every account that has not just passed a
 * real MFA challenge, so the exception below can never fire there. The exception
 * is not "dev mode": it is a 200 from an endpoint that only a named test id can
 * get a 200 from.
 *
 * ── WHAT A 200 ALSO BUYS ────────────────────────────────────────────────────
 *
 * The same call STAMPS `app_metadata.mfa_challenge_at`, bound to this session
 * id, so the bot then satisfies the real SERVER-side step-up gates for the
 * hour, not merely the two browser gates. That is why the session is refreshed
 * afterwards: the stamp only reaches the API on a NEW access token
 * (`use-step-up-challenge.ts` calls that refresh MANDATORY for the same reason).
 * A UI-only flag would have opened the console and then had every gated read
 * inside it refuse, which is a worse walk than no walk.
 *
 * ── THE RULES THIS MODULE KEEPS ─────────────────────────────────────────────
 *
 *   · Asked ONCE per user, ever, per page load. The answer is a fact about the
 *     account, not about a render, and the endpoint rate-limits one stamp per
 *     user per 60 seconds.
 *   · Held IN MEMORY only. Never localStorage, never a cookie: a vouch that
 *     outlived the tab would be a persisted claim about auth posture, which is
 *     exactly the kind of thing this project has been bitten by.
 *   · Only a 200 carrying `ok: true` counts, plus one narrow case: a 429 from
 *     THIS route's own once-a-minute limit, and then only if the refreshed
 *     token carries a fresh stamp bound to this session (see askServer).
 *     Every other status, an unparseable body, a network failure and a thrown
 *     error all mean "no vouch", so the caller behaves exactly as it did
 *     before this file existed.
 *   · Callers must never ask for a session that already has a verified factor,
 *     or for a non-staff session. Those paths are untouched by design.
 */

import { api } from './api/client';
import { supabase } from './supabase';

/** The endpoint whose 200 IS the vouch. See the header for why this one. */
const VOUCH_PATH = '/api/v1/auth/refresh-step-up';

/**
 * How long a caller may hold its screen for the answer.
 *
 * The gate that asks this cannot bounce first and ask afterwards (the redirect
 * unmounts it), so a request that never returns would leave a REAL staff member
 * with no factor looking at a blank console instead of the enrol screen. The
 * browser's own timeout is far too long to be that person's experience, so the
 * vouch gives up on its own and the caller carries on refusing, exactly as it
 * did before this file existed.
 */
const VOUCH_TIMEOUT_MS = 10_000;

/**
 * RAISED FROM 5s TO 10s (2026-09-22, NEW-PAYSTACK-RETURN-ENROL).
 *
 * `refresh-step-up` lives on `func-elreign-onboarding-dev`, a shared Y1
 * Consumption plan. A cold start there has been measured at about 9.6 seconds
 * (the same number that moved the Supabase send-email hook onto its own plan).
 * App Insights shows that app logging "Initializing function HTTP routes" at
 * 17:41:47 SAST, i.e. cold-starting at the exact moment the Paystack return
 * page loaded, so the 5-second give-up could not have been met.
 *
 * COST OF THE CHANGE, plainly: a REAL staff member with no second factor, on a
 * cold function app, now looks at a blank console for up to 10 seconds instead
 * of 5 before the enrol screen appears. Nobody else waits: a session with a
 * factor never asks, and the fresh-stamp short-circuit below means a returning
 * session usually does not ask either. It buys not bouncing a live session onto
 * a screen that mints an unverified factor. No infrastructure changed, so the
 * monthly run rate is unaffected.
 */

/**
 * How long a step-up stamp stays good, in seconds.
 *
 * MIRRORS `STEP_UP_WINDOW_SECONDS` in `backend/src/lib/middleware/step-up.ts`
 * (3600). The browser gate must not consider a stamp valid for one second
 * longer than the server does, or it renders a console whose every gated read
 * then refuses.
 */
export const STEP_UP_WINDOW_SECONDS = 3_600;

interface VouchResponse {
  ok?: unknown;
}

/**
 * The step-up stamp the server wrote onto this user, in epoch seconds, or null.
 *
 * Reads `app_metadata.mfa_challenge_at` off whatever object it is handed. Same
 * validity rules `requireStepUp` applies server side, including the refusal of
 * a future-dated or non-integer claim (arch A.14, clock skew and tampering).
 */
export function readStepUpStampSeconds(appMetadata: unknown): number | null {
  if (!appMetadata || typeof appMetadata !== 'object') return null;
  const raw = (appMetadata as Record<string, unknown>)['mfa_challenge_at'];
  if (typeof raw !== 'number') return null;
  if (!Number.isFinite(raw) || !Number.isInteger(raw) || raw <= 0) return null;
  return raw;
}

/** The claims of a Supabase access token, decoded WITHOUT verification, or
 *  null when the token is absent or not a JWT. Base64url-safe. */
export function accessTokenClaims(accessToken: string | null | undefined): Record<string, unknown> | null {
  if (typeof accessToken !== 'string') return null;
  const part = accessToken.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/**
 * Would the SERVER accept this access token at a step-up gate right now?
 *
 * ── THE SAME RULE AS `requireStepUp`, ALL OF IT (QA P2-9, 2026-10-04) ───────
 *
 * This used to check the stamp's age only, read from `session.user`. The
 * server checks two things (backend/src/lib/middleware/step-up.ts): the stamp
 * is inside the window, AND `app_metadata.mfa_challenge_session_id` equals the
 * token's own `session_id`. The second is what stops a stamp earned by one
 * session (the owner, who just stepped up) from opening the console to another
 * session of the same user (a password-only sign-in elsewhere), because the
 * stamp lives on the USER and rides into every token. A browser gate that
 * skipped it opened the console to exactly the session the server would refuse.
 *
 * Both values are read from the ACCESS TOKEN, which is what the server sees,
 * not from `session.user` (the user object GoTrue returns, which is not the
 * signed token). The browser cannot verify the signature; it does not need to,
 * because a forged token is refused by the API on its first call.
 *
 * What is true about these gates, stated plainly: they are UI. Every
 * step-up-gated route re-checks the same stamp, the same window and the same
 * session binding server side. Ordinary console reads (the client book, for
 * one) do NOT re-check step-up: they authorise off the tenant claim. So for
 * those reads this browser gate is the only thing standing between a staff
 * session with no second factor and the console, which is why it must apply
 * the server's rule exactly and never a looser one.
 */
export function hasFreshStepUpStamp(
  accessToken: string | null | undefined,
  nowSeconds: number = Math.floor(Date.now() / 1000),
): boolean {
  const claims = accessTokenClaims(accessToken);
  if (!claims) return false;
  const meta = claims['app_metadata'];
  const stamped = readStepUpStampSeconds(meta);
  if (stamped === null) return false;
  if (stamped > nowSeconds) return false;
  if (nowSeconds - stamped > STEP_UP_WINDOW_SECONDS) return false;
  // Session binding: both PRESENT and EQUAL. Absent-vs-absent never matches,
  // or a token carrying neither would pass.
  const stampedSession =
    meta && typeof meta === 'object'
      ? (meta as Record<string, unknown>)['mfa_challenge_session_id']
      : undefined;
  const tokenSession = claims['session_id'];
  return (
    typeof stampedSession === 'string' &&
    stampedSession.length > 0 &&
    typeof tokenSession === 'string' &&
    tokenSession.length > 0 &&
    stampedSession === tokenSession
  );
}

/** The HTTP status an `ApiError` carries, or null for an abort, a timeout or a
 *  dropped connection. */
function statusOf(err: unknown): number | null {
  if (!err || typeof err !== 'object') return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? status : null;
}

/** The machine code on an `ApiError`, if any. */
function codeOf(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** The code the vouch route itself answers its once-a-minute limit with
 *  (`backend/src/functions/auth/refresh-step-up.ts`). A 429 without it came
 *  from somewhere else (the gateway, the host) and says nothing about a stamp. */
const VOUCH_ROUTE_RATE_LIMIT_CODE = 'TOO_MANY_REQUESTS';

/**
 * In-memory, one entry, keyed by user id.
 *
 * Keyed rather than a bare boolean so that signing out and in as somebody else
 * inside the same tab cannot inherit the previous account's answer. The promise
 * itself is cached, not just its result, so two gates mounting in the same tick
 * share ONE request instead of racing the rate limiter.
 */
let asked: { userId: string; answer: Promise<boolean> } | null = null;

async function askServer(): Promise<boolean> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), VOUCH_TIMEOUT_MS);
  let body: VouchResponse | undefined;
  try {
    // `useOperatorSession` pins this to the operator's own Supabase session.
    // The question is about the signed-in user's OWN id, and a minted act-as or
    // impersonation token can never satisfy step-up anyway (neither mint stamps
    // `mfa_challenge_at`), so riding an ambient token could only produce a false
    // refusal. `routes/admin/_layout.tsx` already drops act-as context on entry;
    // this is the belt to that pair of braces.
    body = await api<VouchResponse>(VOUCH_PATH, {
      method: 'POST',
      useOperatorSession: true,
      signal: abort.signal,
    });
  } catch (err) {
    // ── A 429 IS A HINT, NOT A YES (QA P3, 2026-10-04) ──────────────────────
    // This read every 429 as "vouched", on the argument that the route only
    // 429s a user it stamped in the last minute. But the gateway and the host
    // can 429 anyone, for any reason, and those say nothing about a stamp. So:
    // only OUR route's own limit counts, and even then it is not taken on
    // trust. The stamp it implies lives on the user record and reaches the API
    // only in a new token, so the session is refreshed and the NEW token must
    // itself carry a fresh stamp bound to this session. Anything else refuses.
    if (statusOf(err) === 429 && codeOf(err) === VOUCH_ROUTE_RATE_LIMIT_CODE) {
      const refreshed = await refreshSessionQuietly();
      return hasFreshStepUpStamp(refreshed);
    }
    // Everything else refuses, silence included. (A branch here used to accept
    // an abort or a dropped connection when the session "already carried a
    // fresh stamp". It could never fire: a session with a fresh stamp is
    // answered by `serverVouchForTestAccount` before this function is called,
    // and a stamp does not get fresher while we wait. Removed rather than kept
    // as a comment about safety that did nothing, QA P3.)
    return false;
  } finally {
    clearTimeout(timer);
  }
  if (body?.ok !== true) return false;

  await refreshSessionQuietly();
  return true;
}

/**
 * The stamp is on the user record; it reaches the API only in a NEW token.
 * Returns that new access token, or null.
 *
 * On the 200 path a refresh failure does not undo the vouch that was just
 * earned, so the browser gates still open; the step-up-gated calls inside the
 * console are what would notice, and they fail closed on their own. On the 429
 * path the new token IS the evidence, so no token means no vouch.
 */
async function refreshSessionQuietly(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.refreshSession();
    return data?.session?.access_token ?? null;
  } catch {
    // Deliberately swallowed. See above.
    return null;
  }
}

/**
 * Does the server vouch for this user id as a step-up test account?
 *
 * Resolves false for every real account, on every environment, and for every
 * account at all on production. Never throws.
 */
export async function serverVouchForTestAccount(
  userId: string | null | undefined,
  /** The session's ACCESS TOKEN (what the server reads), for the fresh-stamp
   *  short-circuit below. */
  accessToken?: string | null,
): Promise<boolean> {
  if (typeof userId !== 'string' || userId.trim() === '') return false;

  // ALREADY STAMPED, SO DO NOT ASK (2026-09-22). The cache below is in memory,
  // so a FULL page load throws it away: coming back from an external payment
  // page asks this endpoint again, on a Consumption plan that may be cold. The
  // stamp the session is already carrying answers the question without a round
  // trip, and it is the same fact the endpoint would re-establish.
  //
  // This is checked BEFORE the cache on purpose: a stamp that has arrived since
  // an earlier refusal is newer information than the refusal.
  if (hasFreshStepUpStamp(accessToken)) return true;

  if (asked !== null && asked.userId === userId) return asked.answer;
  const answer = askServer();
  asked = { userId, answer };
  return answer;
}

/** Test-only: forget the one cached answer between cases. */
export function __resetServerVouchForTests(): void {
  asked = null;
}
